import { ThemeOptions } from '@mui/material';

import { tokens } from '../../../theme/generated/tokens';
import { AbsolutePseudo, focusProps } from '../../../theme/utils';
import { cssVars } from '../../../utils/cssVars';

const { setValues, getters } = cssVars({
  stepSize: '32px',
  labelSpacing: '-23px',
});

const addBorder = (color: string) => ({
  '&:after': {
    ...AbsolutePseudo,
    border: `1px solid ${color}`,
    borderRadius: 'inherit',
  },
});

export const stepperTheme: ThemeOptions['components'] = {
  MuiStepper: {
    styleOverrides: {
      root: () => ({
        '&.DS-Stepper-size': {
          '&Small': setValues({ stepSize: '24px' }),
          '&Medium': setValues({ stepSize: '32px' }),
          '&Large': setValues({ stepSize: '36px' }),
        },
        '& .MuiStepConnector-line': {
          borderColor: tokens.theme.border.structural.neutral,
        },
        '& .MuiStepConnector-root': {
          '&.Mui-active, &.Mui-completed': {
            '& .MuiStepConnector-line': {
              borderColor: tokens.theme.bg.action.primary,
              borderWidth: '2px',
            },
          },
        },
      }),
    },
  },
  // MUI StepLabel uses palette.text.primary for active/completed, which is a
  // static grey.900 and disappears on dark backgrounds. Force token colors.
  MuiStepLabel: {
    styleOverrides: {
      label: {
        '&.Mui-active, &.Mui-completed': {
          color: tokens.theme.fg.structural.primary,
        },
      },
      labelContainer: {
        color: tokens.theme.fg.structural.secondary,
      },
    },
  },
  MuiStep: {
    styleOverrides: {
      root: ({ theme }) => ({
        fontFamily: theme.typography.h5.fontFamily,
        fontWeight: 'bold',
        backgroundColor: tokens.theme.bg.action.primary,
        color: tokens.theme.fg.action.onPrimary,
        borderRadius: tokens.theme.radius.full,
        width: getters.stepSize(),
        height: getters.stepSize(),
        padding: 'unset',
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        boxSizing: 'border-box',
        userSelect: 'none',

        '.DS-Stepper-sizeSmall &': {
          fontSize: '12px',
        },
        '.DS-Stepper-sizeLarge &': {
          fontSize: '20px',
          lineHeight: '24px',
        },
        '& .MuiStepLabel-': {
          '&root': {
            justifyContent: 'center',
          },
          '&labelContainer': {
            position: 'absolute',
            bottom: getters.labelSpacing(),
            justifyContent: 'center',
            width: 'auto',
          },
          '&label': {
            width: 'max-content',
            color: tokens.theme.fg.structural.primary,
            // Override MUI StepLabel active/completed (palette.text.primary = grey.900)
            '&.Mui-active, &.Mui-completed': {
              color: tokens.theme.fg.structural.primary,
            },
            '.DS-Stepper-inverse &': {
              color: tokens.theme.fg.structural.primaryOnInverse,
            },
            '.DS-Stepper-sizeSmall &': theme.typography.P10M,
            '.DS-Stepper-sizeMedium &': theme.typography.P12M,
            '.DS-Stepper-sizeLarge &': theme.typography.P14M,
          },
          '&iconContainer': {
            paddingRight: 'unset',
          },
        },
        '& .DS-Step-statusIcon': {
          display: 'none',
        },
        '&.DS-Step-clickable:not(.Mui-disabled)': {
          '&, *': {
            cursor: 'pointer',
          },
        },
        '&.Mui-completed, &.DS-Step-error, &.DS-Step-loading': {
          '& .DS-Step-statusIcon': {
            display: 'initial',
          },
          '& .DS-Step-content': {
            display: 'none',
          },
        },
        '&.Mui-completed:not(.DS-Step-loading), &.Mui-active:not(.DS-Step-loading)':
          {
            '&:hover': {
              backgroundColor: tokens.theme.bg.action.primaryHover,
            },
            '&:active:not(.Mui-disabled)': {
              backgroundColor: tokens.theme.bg.action.primaryActive,
            },
            '&.Mui-disabled': {
              backgroundColor: tokens.theme.disabled.bg,
            },
          },
        '&:not(.Mui-completed):not(.Mui-active)': {
          backgroundColor: tokens.theme.bg.action.secondary,
          '&:hover': {
            backgroundColor: tokens.theme.bg.action.secondaryHover,
          },
          '& .DS-Step-content': {
            color: tokens.theme.fg.action.onSecondary,
          },
          '&:active': {
            backgroundColor: tokens.theme.bg.action.secondaryActive,
            '& .DS-Step-content': {
              color: tokens.theme.fg.action.onSecondary,
            },
            ...addBorder(tokens.theme.border.action.secondaryActive),
          },
          '& .MuiStepLabel-label': {
            color: tokens.theme.fg.structural.secondary,
            '.DS-Stepper-inverse &': {
              color: tokens.theme.fg.structural.primaryOnInverse,
            },
          },
          '&.Mui-disabled': {
            backgroundColor: tokens.theme.disabled.bg,
            ...addBorder(tokens.theme.disabled.border),
          },
        },
        '&.DS-Step-error': {
          '&:not(.Mui-disabled):not(.DS-Step-loading)': {
            backgroundColor: tokens.theme.bg.action.danger,
            '&:hover': {
              backgroundColor: tokens.theme.bg.action.dangerHover,
            },
            '&:active': {
              backgroundColor: tokens.theme.bg.action.dangerActive,
              ...addBorder(tokens.theme.border.status.error),
            },
          },
          '&.Mui-disabled': {
            backgroundColor: tokens.theme.bg.status.errorSubtle,
          },
        },
        '&:focus-visible': {
          outline: 'none',
          '&:after': {
            ...AbsolutePseudo,
            ...focusProps(),
            borderRadius: 'inherit',
          },
        },
        '&.DS-Step-loading': {
          backgroundColor: tokens.theme.disabled.bg,
          '&.DS-Step-error': {
            backgroundColor: tokens.theme.bg.status.errorSubtle,
          },
          '& .MuiStepLabel-label': {
            color: tokens.theme.fg.structural.secondary,
            '.DS-Stepper-inverse &': {
              color: tokens.theme.fg.structural.primaryOnInverse,
            },
          },
        },
        '& .DS-Step-loadingIcon.DS-Spinner-root': {
          color: tokens.theme.fg.action.onPrimary,
        },
      }),
    },
  },
};
